import React, { useState } from 'react';

import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import Button from 'epn-ui/umd/dist/components/Button';
import Input from 'epn-ui/umd/dist/components/InputText';

import clsx from 'clsx';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';
import colors from 'tailwindcss/colors';

import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faPowerOff } from '@fortawesome/pro-light-svg-icons/faPowerOff';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faPencilAlt } from '@fortawesome/pro-regular-svg-icons/faPencilAlt';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { faTrashRestore } from '@fortawesome/pro-regular-svg-icons/faTrashRestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import api from '@api';
import useI18n from '@i18n';
import sendGaEvent from 'src/utils/metrics/ga';

import { PlacementByOfferIdStatusEnum } from '@pages/creatives/creatives.interface';
import BaseCopyableText from '../../../base/BaseCopyableText';
import ModalBannerCode, { IModalBannerCodeProps } from './ModalBannerCode';
import ModalBannerList, { IModalBannerListProps } from './ModalBannerList';
import ModalDeeplink, { IModalDeepLinkProps } from './ModalDeeplink';
import { IModalPartnersLinkProps, ModalPartnerLink } from './ModalPartnerLink';

import { ICreativeCardProps } from './CreativeCard.interface';

import { MODAL_TYPES } from '../config';

type CardLabelType = {
    [key in 'link' | 'deeplink' | 'banner']: {
        typeName: string;
        color: string;
        bgColor: string;
    };
};

type ModalLinkType = {
    link: React.FC<IModalPartnersLinkProps>;
    deeplink: React.FC<IModalDeepLinkProps>;
    banner: React.FC<IModalBannerListProps>;
};

type ModalByTypes = {
    link:
        | React.FC<IModalPartnersLinkProps>
        | React.FC<IModalDeepLinkProps>
        | React.FC<IModalBannerCodeProps>;
    banner: React.FC<IModalBannerListProps>;
};

type ImageConfig = {
    icon: IconDefinition;
    tooltip: string;
    key: string;
    condition: boolean;
    action: () => void;
};

const CARD_LABEL_TYPES: CardLabelType = {
    link: {
        typeName: 'Link',
        color: colors.blue[500],
        bgColor: 'bg-indigo-100',
    },
    deeplink: {
        typeName: 'DeepLink',
        color: colors.green[500],
        bgColor: 'bg-green-100',
    },
    banner: {
        typeName: 'Banner',
        color: colors.red[400],
        bgColor: 'bg-red-100',
    },
};

const BANNER_TYPE = 'banner';

const MODAL_LINK: ModalLinkType = {
    link: ModalPartnerLink,
    deeplink: ModalDeeplink,
    banner: ModalBannerCode,
};

const CreativeCard: React.FC<ICreativeCardProps> = ({
    creative,
    offer,
    placementUrl,
    isArchive,
    refreshCreatives,
    setIsDeleting,
    isOffersLoading,
    checkedReduction,
    setCheckedReduction,
    activeReduction,
    setActiveReduction,
    hasOrdData,
    placementByOfferIdStatus,
    handleModal,
}) => {
    const { name, link, type, sizes, id } = creative;
    const { t } = useI18n('Creatives');
    const { t: tTools } = useI18n('Tools');
    const { color, typeName, bgColor } = CARD_LABEL_TYPES[type] || {};
    const [editable, setEditable] = useState<boolean>(false);
    const [modalType, setModalType] = useState<keyof typeof MODAL_BY_TYPE | null>(null);
    const [creativeName, setCreativeName] = useState<string>(name);
    const MODAL_BY_TYPE: ModalByTypes = {
        link: MODAL_LINK[type],
        banner: ModalBannerList,
    };

    const ModalComponent = modalType && MODAL_BY_TYPE[modalType];

    const typeIsBanner: boolean = type === BANNER_TYPE;

    const screens = useBreakpoint();
    const isMobile = screens.xs || !screens.md;

    const updateCreativeStatus = async (body: {
        archiveStatus: 'archive' | 'unarchive';
    }): Promise<void> => {
        try {
            setIsDeleting(true);
            // фильтруем по id уже загруженные данные, для отображения сразу
            refreshCreatives(data => data.map(page => page.filter(item => item.id !== id)), false);

            await api.creative.editCreative({ urlParams: { id }, body });

            // после выполнения запроса обновляем список креативов с помощью mutate в запросе useCreatives
            refreshCreatives();
            setIsDeleting(false);
        } catch (err) {
            setIsDeleting(false);
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    const saveSettings = async (): Promise<void> => {
        try {
            refreshCreatives(data => {
                return data.map(page =>
                    page.map(item => {
                        const newCreative = { ...item };
                        if (link) newCreative.name = creativeName;
                        return newCreative;
                    }),
                );
            }, false);

            setEditable(false);

            await api.creative.editCreative({
                urlParams: { id },
                body: { description: creativeName },
            });

            refreshCreatives();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    const ICON_CONFIG: ImageConfig[] = [
        {
            icon: faImages,
            tooltip: 'Banners',
            key: 'banners',
            condition: typeIsBanner && !isArchive,
            action: () => setModalType('banner'),
        },
        {
            icon: faLink,
            tooltip: typeIsBanner ? 'Get Smart-Banner Code' : 'Get Affiliate Link',
            key: 'link',
            condition: !isArchive,
            action: () => setModalType('link'),
        },
        {
            icon: faTrash,
            tooltip: 'Put to archive',
            key: 'archive',
            condition: !isArchive,
            action: () => {
                updateCreativeStatus({ archiveStatus: 'archive' });
                sendGaEvent({
                    eventCategory: 'creative',
                    eventAction: 'archive',
                });
            },
        },
        {
            icon: faTrashRestore,
            tooltip: 'Restore',
            key: 'restore',
            condition: isArchive,
            action: () => updateCreativeStatus({ archiveStatus: 'unarchive' }),
        },
    ];

    const filteredIcons: ImageConfig[] = ICON_CONFIG.filter(({ condition }) => condition);

    const placementByOfferIsWaiting: boolean =
        placementByOfferIdStatus === PlacementByOfferIdStatusEnum.WAITING ||
        placementByOfferIdStatus === PlacementByOfferIdStatusEnum.REVIEW;
    const creativeHasAllowedPlacementByOffer: boolean =
        placementByOfferIdStatus === PlacementByOfferIdStatusEnum.ALLOW;
    const creativeHasPlacementId: boolean = creative.extInfoType.placementId.length > 0;
    /**
     * 1) у веб-мастера заполнены юридические данные
     * 2) оффер креатива имеет активный договор
     * 3) оффер креатива активный
     * 4) erid пустой
     * 5) не через инструмент маркировки
     */
    const offerIsActive: boolean = offer?.status === 'active';
    const creativeActive: boolean = hasOrdData && offer?.hasContracts && offerIsActive;
    const showGetEridTokenBtn: boolean =
        creativeActive && !creative.erid?.length && !creative.extInfoType.madeInMarking;
    const eridNotEmpty = typeof creative.erid === 'string' && creative.erid.length > 0;
    const articleNotEmpty =
        typeof creative?.extInfoType?.aliArticle === 'string' &&
        creative?.extInfoType?.aliArticle.length > 0;

    const handleGetErid = async (): Promise<void> => {
        // Успешный кейс
        if (creativeHasPlacementId) {
            handleModal(MODAL_TYPES.getEridToken, {
                creative,
                offer,
            });
        }
        // У креатива нет площадки, но есть подтверженная площадка к офферу креатива
        if (!creativeHasPlacementId && creativeHasAllowedPlacementByOffer) {
            handleModal(MODAL_TYPES.selectPlacement, {
                creative,
                offer,
            });
        }
        // У креатива нет площадки, нет подтвержденной площадки к офферу
        if (!creativeHasPlacementId && !creativeHasAllowedPlacementByOffer) {
            handleModal(MODAL_TYPES.addPlacement, { offer });
        }
    };

    return (
        <>
            <div className="flex flex-col justify-between w-full h-full border p-[16px] min-w-[260px] xs:min-w-full border-blueGray-100 rounded-[6px]">
                <div>
                    <div className="relative flex flex-wrap items-center justify-between">
                        <TooltipPro placement="top" title={creativeName}>
                            <div className="relative flex items-center max-w-1/2 group">
                                {editable ? (
                                    <>
                                        <Input
                                            id={`CreativeCardNameInput${id}`}
                                            value={creativeName}
                                            onChange={e => setCreativeName(e.target.value)}
                                        />
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            id={`CreativeCardSaveButton${id}`}
                                            className="absolute right-0 ml-8 text-green-500 cursor-pointer"
                                            onClick={saveSettings}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <p
                                            id={`CreativeCardNameText${id}`}
                                            className={clsx(
                                                'font-medium truncate whitespace-nowrap text-p1 text-blueGray-600',
                                                { 'opacity-70': isArchive },
                                            )}
                                        >
                                            {creativeName}
                                        </p>
                                        <span className="opacity-0 ml-1.5 group-hover:opacity-100">
                                            <FontAwesomeIcon
                                                id={`CreativeCardEditButton${id}`}
                                                className={clsx(
                                                    'mr-4 cursor-pointer text-blueGray-300 text-p1 hover:text-blueGray-500',
                                                    { 'opacity-70': isArchive },
                                                )}
                                                icon={faPencilAlt}
                                                onClick={() => setEditable(true)}
                                            />
                                        </span>
                                    </>
                                )}
                            </div>
                        </TooltipPro>
                        <div className="flex text-right gap-1.5">
                            {creative.extInfoType.madeInMarking && (
                                <span
                                    style={{ color: colors.yellow[400] }}
                                    className={clsx(
                                        `font-medium text-p3 py-[3px] px-[8px] bg-yellow-50 rounded-[3px]`,
                                        {
                                            'opacity-70': isArchive,
                                        },
                                    )}
                                >
                                    {tTools('Marking')}
                                </span>
                            )}
                            <span
                                style={{ color }}
                                className={clsx(
                                    `font-medium text-p3 py-[3px] px-[8px] rounded-[3px]`,
                                    {
                                        'opacity-70': isArchive,
                                        [bgColor]: bgColor,
                                    },
                                )}
                            >
                                {t(typeName)}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <TooltipPro title={link} placement="top">
                            <div
                                className={clsx(
                                    'mt-4 text-p2 text-blueGray-600 min-h-5 px-[8px] py-[6px] rounded-[6px] bg-blueGray-100',
                                    {
                                        'opacity-70': isArchive,
                                    },
                                )}
                            >
                                <p className="truncate text-blueGray-300">
                                    <span className="font-medium text-blueGray-500">
                                        {t('Page')}:
                                    </span>{' '}
                                    <span>{link}</span>
                                </p>
                            </div>
                        </TooltipPro>
                        <TooltipPro title={placementUrl || t('Not specified')} placement="top">
                            <div
                                className={clsx(
                                    'text-p2 text-blueGray-600 min-h-5 px-[8px] py-[6px] rounded-[6px] bg-blueGray-100',
                                    {
                                        'opacity-70': isArchive,
                                    },
                                )}
                            >
                                <p className="truncate">
                                    <span className="font-medium text-blueGray-500">
                                        {t('Placement')}:
                                    </span>{' '}
                                    <span className="lowercase text-blueGray-300">
                                        {placementUrl || t('Not specified')}
                                    </span>
                                </p>
                            </div>
                        </TooltipPro>
                        {(eridNotEmpty || articleNotEmpty) && (
                            <div className="flex flex-col xs:flex-row gap-2">
                                {articleNotEmpty && (
                                    <div
                                        className={clsx(
                                            'w-full text-p2 text-blueGray-600 min-h-5 px-[8px] py-[6px] rounded-[6px] bg-blueGray-100',
                                            {
                                                'opacity-70': isArchive,
                                            },
                                        )}
                                    >
                                        <BaseCopyableText
                                            label={t('Article')}
                                            copyableText={creative?.extInfoType?.aliArticle}
                                            truncate
                                        >
                                            {creative?.extInfoType?.aliArticle}
                                        </BaseCopyableText>
                                    </div>
                                )}
                                {eridNotEmpty && (
                                    <div
                                        className={clsx(
                                            'w-full text-p2 text-blueGray-600 min-h-5 px-[8px] py-[6px] rounded-[6px] bg-blueGray-100',
                                            {
                                                'opacity-70': isArchive,
                                            },
                                        )}
                                    >
                                        <BaseCopyableText
                                            label={t('Token')}
                                            copyableText={creative.erid}
                                            truncate
                                        >
                                            {creative.erid}
                                        </BaseCopyableText>
                                    </div>
                                )}
                                {showGetEridTokenBtn && placementByOfferIsWaiting ? (
                                    <TooltipPro
                                        title={t('Awaiting placement approval')}
                                        placement="top"
                                    >
                                        <Button
                                            className="w-full !bg-blueGray-300 !border-blueGray-300 xs:w-[188px]"
                                            size="small"
                                            type="primary"
                                            onClick={e => {
                                                e.preventDefault();
                                            }}
                                        >
                                            {t('Get token')}
                                        </Button>
                                    </TooltipPro>
                                ) : null}
                                {showGetEridTokenBtn && !placementByOfferIsWaiting ? (
                                    <Button
                                        className="w-full !bg-blueGray-800 !border-blueGray-800 xs:w-[188px]"
                                        size="small"
                                        type="primary"
                                        onClick={handleGetErid}
                                    >
                                        {t('Get token')}
                                    </Button>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center justify-between mt-4 mb-0">
                    <div className="flex">
                        {filteredIcons.map(({ icon, tooltip, key, action }) => (
                            <TooltipPro
                                key={key}
                                title={t(tooltip)}
                                placement="top"
                                trigger={isMobile ? [] : ['hover']}
                            >
                                <span className={isArchive ? 'opacity-70' : ''}>
                                    <FontAwesomeIcon
                                        id={`CreativeCardIcon${key}${id}`}
                                        className={clsx(
                                            'mr-4 cursor-pointer text-blueGray-300 text-p1 hover:text-blueGray-500',
                                            { 'opacity-70': isArchive },
                                        )}
                                        icon={icon}
                                        onClick={action}
                                    />
                                </span>
                            </TooltipPro>
                        ))}
                        {typeIsBanner && (
                            <p className="px-2 truncate py-0.5 bg-blueGray-100 rounded-[3px]">
                                <span className="text-blueGray-300">{sizes}</span>
                            </p>
                        )}
                    </div>

                    {isOffersLoading && (
                        <div className="w-24 h-4 mb-2 bg-gray-300 rounded-2 animate-pulse" />
                    )}

                    {!isOffersLoading && offer?.image && (
                        <img
                            src={offer.image}
                            alt={offer.name}
                            width="96"
                            height="32"
                            className={clsx('-mr-2.5 w-24 h-8', { 'opacity-70': isArchive })}
                        />
                    )}

                    {!isOffersLoading && !offerIsActive && (
                        <div className="flex items-center text-coolGray-300 text-p2">
                            <FontAwesomeIcon icon={faPowerOff} className="mr-1" />
                            {t('Disabled')}
                        </div>
                    )}
                </div>
            </div>
            {ModalComponent && (
                <ModalComponent
                    data={creative}
                    onClose={() => setModalType(null)}
                    checkedReduction={checkedReduction}
                    setCheckedReduction={setCheckedReduction}
                    activeReduction={activeReduction}
                    setActiveReduction={setActiveReduction}
                />
            )}
        </>
    );
};

export default CreativeCard;
